import { QuestionCircleOutlined, StarFilled } from '@ant-design/icons'
import { Alert, Badge, Card, Collapse, Descriptions, Flex, List, Tag, Timeline, Tooltip, Typography } from 'antd'
import { ModelRadarChart } from './ModelRadarChart'

const DIMENSION_NAMES = {
  V1: '目的地障碍',
  V2: '执行负担',
  V3: '目的地独特体验',
  V4: '意义与连接',
}

const DIMENSION_TITLES = {
  V1: 'V1 目的地障碍',
  V2: 'V2 执行负担（客人痛点）',
  V3: 'V3 目的地独特体验',
  V4: 'V4 意义与连接（客人出行动机）',
}

const SUFFICIENCY_ALERT_TYPE = {
  sufficient: 'success',
  partially_sufficient: 'warning',
  insufficient: 'error',
}

const SUFFICIENCY_STATUS = {
  sufficient: { label: '充分', color: 'success' },
  partially_sufficient: { label: '部分充分', color: 'warning' },//这个partially_sufficient是猜的
  insufficient: { label: '不足', color: 'error' },//这个insufficient是猜的
}

const STATUS_TAGS = {
  confirmed: { label: '已确认', color: 'green' },
  mixed: { label: '已确认+待核验', color: 'orange' },
  inferred: { label: '推断', color: 'blue' },
  needs_verification: { label: '待核验', color: 'red' },
  pending_confirmation: { label: '待确认', color: 'red' },
  unknown: { label: '未知', color: 'default' },
  proposed: { label: '建议', color: 'gold' },
}

const renderStatusTag = (status) => {
  const meta = STATUS_TAGS[status] || { label: status, color: 'default' }
  return <Tag color={meta.color}>{meta.label}</Tag>
}

const renderPending = (text) =>
  text ? (
    <Alert
      type='warning'
      showIcon={false}
      style={{ borderStyle: 'dashed', borderColor: '#faad14' }}
      message={
        <Flex gap={4} align='start'>
          <Tag color='processing'>待确认</Tag>
          <Typography.Text>{text}</Typography.Text>
        </Flex>
      }
    />
  ) : null

const SectionHeading = ({ title, meta }) => (
  <Flex wrap gap={7} align='center' style={{ marginBottom: 10 }}>
    <Typography.Text strong>{title}</Typography.Text>
    {meta && <Typography.Text type='secondary' style={{ marginLeft: 'auto', fontSize: 11 }}>{meta}</Typography.Text>}
  </Flex>
)

const Subtitle = ({ children, extra }) => (
  <Flex align='center' gap={6} style={{ margin: '12px 0 8px' }}>
    <Badge color='#1ba784' />
    <Typography.Text strong>{children}</Typography.Text>
    {extra}
  </Flex>
)

const circleDotStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: '#1ba784',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
}

const renderIndexDot = (n) => <span style={circleDotStyle}>{n}</span>

const renderDiamondDot = () => (
  <span style={{ ...circleDotStyle, transform: 'rotate(45deg)', borderRadius: 3, background: '#0f766e' }}>
    <StarFilled style={{ transform: 'rotate(-45deg)', fontSize: 9 }} />
  </span>
)

const Copilot5V = ({ data, updatedAt, showHeader = true }) => {
  const { information_sufficiency: sufficiency = {}, v5_conclusion: v5 = {}, radar = {}, insight_summary: insights = [], how = {}, dimension_details: dimensionDetails = [] } = data
  const mainCode = v5?.main_dimension?.code || ''

  const radarItems = (radar.points || []).map((point) => ({
    key: point.dimension,
    intensity: point.priority_score * 20,
    status: point.evidence_status,
    dominant: point.dimension === radar.main_dimension,
    name: DIMENSION_NAMES[point.dimension] || '',
    summary: point.basis_summary,
  }))

  const decisionSection = (
    <Flex vertical gap={10}>
      <Flex wrap gap={7} align='center'>
        <Typography.Text strong>V5｜本次旅行的意义与期待</Typography.Text>
      </Flex>
      {v5.meaning_and_expectation ? <Alert type='success' showIcon={false} description={v5.meaning_and_expectation} /> : '空'}
      {/* <Flex wrap gap={6} align='center'>
        <Typography.Text strong type='success'>主导维度</Typography.Text>
        <Tag color='red'>{v5.main_dimension.code} · {v5.main_dimension.field}</Tag>
      </Flex>
      <Typography.Paragraph style={{ marginBottom: 0 }}>{v5.main_dimension.reason}</Typography.Paragraph>
      <Subtitle>核心设计判断</Subtitle>
      <Typography.Paragraph style={{ marginBottom: 0 }}>{v5.design_judgment}</Typography.Paragraph> */}
    </Flex>
  )

  const radarLegend = (
    <Flex wrap justify='center' gap='5px 10px'>
      <Flex align='center' gap={4}>
        <span style={{ display: 'inline-block', width: 13, borderTop: '2px solid #c41230' }} />
        <Typography.Text type='secondary' style={{ fontSize: 11 }}>已确认（实线）</Typography.Text>
      </Flex>
      <Flex align='center' gap={4}>
        <span style={{ display: 'inline-block', width: 13, borderTop: '2px dashed #b88a90' }} />
        <Typography.Text type='secondary' style={{ fontSize: 11 }}>待核验 / 部分确认（虚线）</Typography.Text>
      </Flex>
      <Flex align='center' gap={4}>
        <StarFilled style={{ color: '#c41230', fontSize: 11 }} />
        <Typography.Text type='secondary' style={{ fontSize: 11 }}>主导维度</Typography.Text>
      </Flex>
    </Flex>
  )

  const radarSection = (
    <Flex vertical gap={8}>
      <SectionHeading title='雷达图' />
      <div style={{ maxWidth: 300, width: '100%', margin: '0 auto' }}>
        <ModelRadarChart items={radarItems} dominantId={radar.main_dimension} />
      </div>
      {radarLegend}
    </Flex>
  )

  const insightsSection = (
    <Flex vertical>
      <SectionHeading title='需求洞察' />
      {insights.map((insight) => (
        <Flex key={insight.insight_id} gap={7} align='flex-start' style={{ marginBottom: 10 }}>
          <Tag color={insight.dimension === mainCode ? 'red' : 'default'}>{insight.dimension}</Tag>
          <Flex vertical flex={1} style={{ minWidth: 0 }}>
            <Flex wrap gap={6} align='center'>
              <Typography.Text strong>{insight.headline}</Typography.Text>
              {renderStatusTag(insight.evidence_status)}
            </Flex>
            <Typography.Paragraph style={{ marginBottom: 0 }}>{insight.summary}</Typography.Paragraph>
            {renderPending(insight.pending_confirmation)}
          </Flex>
        </Flex>
      ))}
    </Flex>
  )

  const howSection = (
    <Flex vertical>
      <SectionHeading title='行程设计建议' />
      <Typography.Paragraph style={{ marginBottom: 12 }}>整体思路：{how.overall_idea}</Typography.Paragraph>
      {(how.routes || []).map((route) => (
        <Flex vertical key={route.route_id}>
          <Subtitle extra={route.recommended ? <Tag color='green'>路线推荐</Tag> : null}>
            {route.name}
          </Subtitle>
          {/* <Typography.Paragraph style={{ marginBottom: 12 }}>{route.fit}</Typography.Paragraph> */}
          <Flex align='center' gap={8} style={{ overflowX: 'auto', paddingBottom: 8 }}>
            {(route.segments || []).map((segment, idx) => (
              <Flex key={`${route.route_id}-${idx}`} align='center' gap={8} style={{ flexShrink: 0 }}>
                <div
                  style={{
                    flexShrink: 0,
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    padding: '8px 12px',
                    background: '#fafafa',
                    minWidth: 120,
                    maxWidth: 220,
                  }}
                >
                  <Flex vertical gap={2}>
                    <Typography.Text strong>{segment.place_or_segment}</Typography.Text>
                    <Typography.Text type='success'>{segment.suggested_duration}  {renderStatusTag(segment.status)}</Typography.Text>
                  </Flex>
                </div>
                {idx < route.segments.length - 1 && (
                  <span style={{ flexShrink: 0, color: '#999', fontSize: 16 }}>→</span>
                )}
              </Flex>
            ))}
          </Flex>
        </Flex>
      ))}
      {(how.experience_suggestions || []).length > 0 && (
        <>
          <Subtitle>体验建议</Subtitle>
          <List
            size='small'
            dataSource={how.experience_suggestions || []}
            renderItem={(suggestion) => (
              <List.Item>
                <Flex vertical gap={2}>
                  <Flex wrap gap={6} align='center'>
                    <Typography.Text strong>{suggestion.title}</Typography.Text>
                    {renderStatusTag(suggestion.status)}
                  </Flex>
                  <Typography.Text>{suggestion.execution}</Typography.Text>
                  <Typography.Text type='secondary'>契合原因：{suggestion.why_it_fits}</Typography.Text>
                </Flex>
              </List.Item>
            )}
          />
        </>
      )}
      {(how.selling_points || []).length > 0 && (
        <>
          <Subtitle>下一步沟通建议</Subtitle>
          <List
            size='small'
            dataSource={how.selling_points || []}
            renderItem={(point) => <List.Item>{point}</List.Item>}
          />
        </>
      )}
      {/* {how.next_step && (
        <Alert type='success' showIcon={false} message='下一步' description={how.next_step} style={{ marginTop: 12 }} />
      )} */}
    </Flex>
  )

  const detailsSection = (
    <Collapse
      size='small'
      defaultActiveKey={[]}
      items={[
        {
          key: 'v1v4',
          label: (
            <Flex wrap gap={7} align='center' style={{ width: '100%' }}>
              <Typography.Text strong>V1–V4 需求详情</Typography.Text>
              <Typography.Text type='secondary' style={{ marginLeft: 'auto', fontSize: 11 }}>V4 → V3 → V2 → V1</Typography.Text>
            </Flex>
          ),
          children: (
            <Flex vertical gap={10}>
              {dimensionDetails.map((dimension) => (
                <Card key={dimension.dimension} size='small' variant='outlined' style={dimension.is_main_dimension ? { borderColor: '#1ba784' } : { borderColor: '#d9d9d9' }}>
                  <Flex wrap gap={6} align='center' style={{ marginBottom: 8 }}>
                    <Tag color={dimension.dimension === mainCode ? 'red' : 'default'}>{dimension.dimension}</Tag>
                    <Typography.Text strong>{DIMENSION_TITLES[dimension.dimension]}</Typography.Text>
                    {dimension.is_main_dimension && <Tag color='red'>主导维度</Tag>}
                  </Flex>
                  {(dimension.detail_items || []).map((item) => (
                    <Flex vertical key={item.detail_item_id} style={{ marginTop: 10 }}>
                      <Flex wrap gap={6} align='center'>
                        <Typography.Text strong>{item.field} · {item.title}</Typography.Text>
                        {renderStatusTag(item.status)}
                      </Flex>
                      <Descriptions
                        size='small'
                        column={1}
                        style={{ marginTop: 5 }}
                        items={[
                          {
                            key: 'facts',
                            label: '事实',
                            children: item.facts.length > 0
                              ? <Flex vertical>{item.facts.map((fact) => <Typography.Text key={fact}>{fact}</Typography.Text>)}</Flex>
                              : '暂无已确认事实，以下为分析与待核验说明，不做虚构补充。',
                          },
                          { key: 'interpretation', label: '解读', children: item.interpretation },
                          { key: 'recommendation', label: '建议', children: item.recommendation },
                        ]}
                      />
                      {renderPending(item.pending_confirmation)}
                    </Flex>
                  ))}
                </Card>
              ))}
            </Flex>
          ),
        },
      ]}
    />
  )

  const content = (
    <Flex vertical gap={8}>
      {sufficiency.status !== 'sufficient' && (
        <Alert
          type={SUFFICIENCY_ALERT_TYPE[sufficiency.status] || 'info'}
          showIcon={false}
          style={{ padding: '4px 10px',margin: '15px' }}
          message={
            <Flex gap={6} align='center' wrap>
              <Tag color={SUFFICIENCY_STATUS[sufficiency.status]?.color} style={{ marginInlineEnd: 0 }}>{SUFFICIENCY_STATUS[sufficiency.status]?.label || sufficiency.status}</Tag>
              <Typography.Text style={{ fontSize: 12 }}>{sufficiency.reason}</Typography.Text>
            </Flex>
          }
        />
      )}
      <Timeline
        items={[
          { dot: renderDiamondDot(), children: decisionSection },
          { dot: renderIndexDot(1), children: radarSection },
          { dot: renderIndexDot(2), children: howSection },
          { dot: renderIndexDot(3), children: insightsSection },
        ]}
      />
      {detailsSection}
      <Flex vertical align='center' gap={2} style={{ paddingTop: 5 }}>
        <Typography.Text type='secondary' style={{ fontSize: 11 }}>最近更新：{updatedAt}</Typography.Text>
      </Flex>
    </Flex>
  )

  if (!showHeader) {
    return <div style={{ padding: 10 }}>{content}</div>
  }

  return (
    <div style={{ padding: 10 }}>
      <Card
        size='small'
        variant='outlined'
        title={<Typography.Text strong>5V价值</Typography.Text>}
        extra={<Tooltip title='什么是 5V 价值模型？'><QuestionCircleOutlined /></Tooltip>}>
        {content}
      </Card>
    </div>
  )
}

export default Copilot5V
